import { ApolloQueryResult } from '@apollo/client/index.js';
import { GetMePatientQuery, useGetMePatientQuery } from '@pm/graphql';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import {
  createContext,
  PropsWithChildren,
  use,
  useEffect,
  type FC,
} from 'react';
import { LoadingPage } from '../pages/LoadingPage';
import { identifyLDUser } from '../utilities/identifyLDUser';

type PatientProfileContextType = {
  profile: NonNullable<Required<GetMePatientQuery>['me']>;
  refetch: () => Promise<ApolloQueryResult<GetMePatientQuery>>;
  loading: boolean;
};

export const PatientProfileContext = createContext<
  PatientProfileContextType | undefined
>(undefined);

export const usePatientProfileContext = () => use(PatientProfileContext);

const setOrRemove = (storage: Storage, key: string, value?: string) => {
  if (value) {
    storage.setItem(key, value);
  } else {
    storage.removeItem(key);
  }
};

export const PatientProfileProvider: FC<PropsWithChildren> = ({ children }) => {
  const ldClient = useLDClient();
  const { data, refetch, loading } = useGetMePatientQuery();

  // These four feed LaunchDarkly's user context. LDUserLocation only
  // re-identifies when the route or auth state changes, so the query resolving
  // doesn't reach it; identify here once the values are written. Writing them
  // in an effect rather than during render also means HAS_RECEIVED_RX is no
  // longer set to "false" on the passes before the query resolves.
  useEffect(() => {
    if (!data?.me) return;

    setOrRemove(
      localStorage,
      'PROVINCE',
      data.me.profile?.province?.code ?? undefined,
    );
    setOrRemove(
      localStorage,
      'COUNTRY',
      data.me.profile?.province?.country?.code ?? undefined,
    );

    const hasReceivedRX = (data.me.workflowSteps ?? []).some(
      (step) => step.type === 'Steps::FulfillPrescription',
    );
    sessionStorage.setItem('HAS_RECEIVED_RX', hasReceivedRX.toString());

    if (data.me.id) {
      sessionStorage.setItem('USER_ID', data.me.id);
    }

    if (ldClient) {
      identifyLDUser(ldClient, true);
    }
  }, [data, ldClient]);

  if (!data?.me) {
    return loading ? <LoadingPage /> : null;
  }

  return (
    <PatientProfileContext
      value={{
        profile: data.me,
        refetch,
        loading,
      }}
    >
      {children}
    </PatientProfileContext>
  );
};
